import { equals, ifElse, indexOf, last, map, path, pipe, prop, sortBy, takeLastWhile, takeWhile } from 'ramda';
import { createSelector } from 'reselect';

import { JumpTo, TEntities } from './withItemsReducer.types';
import { MediaManagerSDKAnyItem } from '../../media-manager-sdk';
import { RelatedItemState } from '../withRelatedItems/withRelatedItems.types';

const getItemEntities = path(['entities', 'items']);
const getRelatedItemEntities = path(['entities', 'relatedItems']);
export const getItemIds = prop('items');
export const getRelatedItemIds = prop('relatedItems');
export const getSelectedItemIds = prop('selectedItems');

export const getItem = createSelector([getItemEntities, getItemIds], (items) => (id) => items[id]);
export const getRelatedItem = createSelector([getRelatedItemEntities, getRelatedItemIds], (items) => (id) => items[id]);

export const getItems = createSelector([getItemEntities, getItemIds], (items, ids) =>
  map((id: string) => items[id], ids),
);

export const getRelatedItems = createSelector([getRelatedItemEntities, getRelatedItemIds], (items, ids) =>
  map((id: string) => items[id], ids),
);

interface ItemKey {
  siteMediaToken: string;
  id: string;
}

const getItemId = (id: string, items: Record<string, any>): string | undefined => {
  // if Id is guid
  if (items[id]) {
    return id;
  }
  // in FilesImporter id from data-hook is actually json
  const matchingKey = Object.keys(items).find((key) => {
    try {
      const parsed: ItemKey = JSON.parse(key);
      return parsed.id === id;
    } catch {
      return key === id;
    }
  });

  if (matchingKey) {
    try {
      const parsed: ItemKey = JSON.parse(matchingKey);
      // key in items will be stringifed json
      return JSON.stringify({ siteMediaToken: parsed.siteMediaToken, id });
    } catch {
      return matchingKey;
    }
  }

  return undefined;
};

export const getSelectedItems = createSelector(
  [getItemEntities, getRelatedItemEntities, getSelectedItemIds],
  (items, relatedItems, ids) => map((id: string) => items[getItemId(id, items)] || relatedItems[id], ids),
);

type TSelectItemToJump = (props: {
  itemId: string;
  items: string[];
  jumpTo: JumpTo;
  entities: TEntities;
  activeRelatedItemsState?: RelatedItemState;
  isSelectingRelatedItem?: boolean;
}) => MediaManagerSDKAnyItem;

const getItemDataHook = (itemId) => `[data-hook-item-id="${itemId}"]:not([data-hook-item-type="related_item"])`;

const getElementDomRect = (itemId: string): ClientRect | DOMRect => {
  let realId = itemId;
  try {
    realId = JSON.parse(itemId).id ?? itemId; // import from site actually has JSON in item id
  } catch {}

  const domItem = document.querySelector(getItemDataHook(realId));

  if (!domItem) {
    throw new Error(`can't find DOM element with id: ${realId}`);
  }

  return domItem.getBoundingClientRect();
};

interface IGetItemsUnderOrAboveSelected {
  selectedItemRect: ClientRect | DOMRect;
  jumpTo: JumpTo;
  items: string[];
  index: number;
  activeRelatedItemsState?: RelatedItemState;
}

const getCrossedItemsUnderOrAboveSelected = ({
  selectedItemRect,
  jumpTo,
  items,
  index,
  activeRelatedItemsState,
}: IGetItemsUnderOrAboveSelected): string[] => {
  const isBottomJump = jumpTo === JumpTo.Bottom;
  const newPossibleItems = [];
  let isFileFound = false;

  const isCrossedItem = (item) => {
    try {
      const { top, bottom, left, right } = getElementDomRect(item);

      const diff = isBottomJump ? top - selectedItemRect.bottom : bottom - selectedItemRect.top;

      const isCrossedItem = left < selectedItemRect.right && right > selectedItemRect.left;

      let isNeededRow = Math.abs(diff) < selectedItemRect.height;

      if (activeRelatedItemsState && !isNeededRow) {
        isNeededRow = Math.abs(diff) < selectedItemRect.height * 2;
      }

      if (isNeededRow && isCrossedItem) {
        isFileFound = true;

        newPossibleItems.push(item);
        return true;
      }

      return !isFileFound;
    } catch (error) {
      return false;
    }
  };

  if (isBottomJump) {
    takeWhile(isCrossedItem, items.slice(index + 1));
  } else {
    takeLastWhile(isCrossedItem, items.slice(0, index));
  }

  return newPossibleItems;
};

interface IGetItemForSelect {
  selectedItemRect: ClientRect | DOMRect;
  newPossibleItems: string[];
}

interface IWithCoverageSizeItem {
  item: string;
  coverageSize: number;
}

const getItemForSelect = ({ selectedItemRect, newPossibleItems }: IGetItemForSelect): string =>
  pipe<string[], IWithCoverageSizeItem[], IWithCoverageSizeItem[], IWithCoverageSizeItem, string>(
    map((item) => {
      try {
        const { left, right } = getElementDomRect(item);

        if (right <= selectedItemRect.right) {
          return { coverageSize: right - selectedItemRect.left, item };
        }

        if (left >= selectedItemRect.left) {
          return { coverageSize: selectedItemRect.right - left, item };
        }

        return { coverageSize: 0, item };
      } catch (error) {
        return { coverageSize: 0, item };
      }
    }),
    sortBy(prop('coverageSize')),
    last,
    prop('item'),
  )(newPossibleItems);

export const selectItemToJump: TSelectItemToJump = ({
  itemId,
  items,
  jumpTo,
  entities,
  activeRelatedItemsState,
  isSelectingRelatedItem,
}) => {
  const index = indexOf(itemId, items);

  const defaultItem = entities.items[itemId];

  if (jumpTo === JumpTo.Bottom || jumpTo === JumpTo.Top) {
    if (isSelectingRelatedItem) {
      return defaultItem;
    }

    try {
      const selectedItemRect = getElementDomRect(itemId);

      const newPossibleItems = getCrossedItemsUnderOrAboveSelected({
        selectedItemRect,
        jumpTo,
        items,
        index,
        activeRelatedItemsState,
      });

      const countNewPossibleItems = newPossibleItems.length;

      if (!countNewPossibleItems) {
        return defaultItem;
      }

      if (countNewPossibleItems === 1) {
        return entities.items[newPossibleItems[0]];
      }

      return entities.items[getItemForSelect({ newPossibleItems, selectedItemRect }) || itemId];
    } catch (error) {
      return defaultItem;
    }
  }

  const newPossibleItem = ifElse(
    equals(JumpTo.Prev),
    () => (index !== 0 ? entities.items[items[index - 1]] : null),
    () => (index !== items.length - 1 ? entities.items[items[index + 1]] : null),
  )(jumpTo);

  return newPossibleItem || defaultItem;
};
